import { motion } from "framer-motion";
import { BookOpen, PlayCircle, Sparkles, Award, Zap } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export default function CourseCurriculum({ curriculum = [], totalSessions, level }) {
  if (!curriculum.length) return null;

  const moduleCount = curriculum.length;
  const sessionCount = curriculum.reduce((acc, m) => acc + m.sessions.length, 0);
  let sessionCursor = 0;

  return (
    <section
      data-testid="section-curriculum"
      className="py-14 sm:py-20 bg-gradient-to-b from-transparent to-slate-100/60 dark:to-white/[0.02]"
    >
      <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
        >
          <span className="text-[11px] uppercase tracking-[0.24em] text-[#2563EB] dark:text-[#06B6D4] font-semibold">
            Inside the course
          </span>
          <h2
            data-testid="curriculum-title"
            className="font-display mt-3 text-4xl sm:text-5xl tracking-tighter font-semibold text-[#0F172A] dark:text-white text-balance leading-[1.05]"
          >
            Course Curriculum
          </h2>
          <p className="mt-4 text-slate-600 dark:text-slate-300 max-w-2xl">
            A complete, structured roadmap — from your first cut to landing
            paying clients. Every module is project-based.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-2.5">
            <span
              data-testid="curriculum-badge-sessions"
              className="inline-flex items-center gap-1.5 rounded-full border border-[#06B6D4]/40 bg-[#06B6D4]/10 px-3 py-1.5 text-xs font-semibold text-[#0F172A] dark:text-[#06B6D4]"
            >
              <PlayCircle className="h-3.5 w-3.5" /> {totalSessions || `${sessionCount}+ Sessions Included`}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 dark:border-white/10 bg-white dark:bg-white/[0.04] px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-200">
              <BookOpen className="h-3.5 w-3.5" /> {moduleCount} Modules
            </span>
            {level && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 dark:border-white/10 bg-white dark:bg-white/[0.04] px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-200">
                <Award className="h-3.5 w-3.5" /> {level}
              </span>
            )}
          </div>
        </motion.div>

        {/* Accordion */}
        <div className="mt-10">
          <Accordion
            type="multiple"
            defaultValue={["module-0"]}
            data-testid="curriculum-accordion"
            className="space-y-3"
          >
            {curriculum.map((mod, i) => {
              const start = sessionCursor + 1;
              const end = sessionCursor + mod.sessions.length;
              sessionCursor = end;

              return (
                <motion.div
                  key={mod.title}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.35, delay: i * 0.04 }}
                >
                  <AccordionItem
                    value={`module-${i}`}
                    data-testid={`curriculum-module-${i}`}
                    className={`rounded-2xl border bg-white dark:bg-white/[0.04] overflow-hidden hairline transition-all duration-300 hover:border-[#06B6D4]/40 ${
                      mod.bonus
                        ? "border-amber-400/40 bg-gradient-to-r from-amber-50/60 to-transparent dark:from-amber-500/10"
                        : "border-slate-200/80 dark:border-white/10"
                    }`}
                  >
                    <AccordionTrigger
                      data-testid={`curriculum-module-trigger-${i}`}
                      className="px-5 sm:px-7 py-5 hover:no-underline text-left group"
                    >
                      <div className="flex items-center gap-4 sm:gap-5 w-full min-w-0">
                        <span
                          className={`flex-none inline-flex h-10 w-10 sm:h-11 sm:w-11 items-center justify-center rounded-xl font-display font-semibold text-sm ${
                            mod.bonus
                              ? "bg-amber-400 text-[#0F172A]"
                              : "bg-[#0F172A] text-white dark:bg-white dark:text-[#0F172A]"
                          }`}
                        >
                          {mod.bonus ? <Zap className="h-4 w-4" /> : String(i + 1).padStart(2, "0")}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span
                              className={`text-[10px] uppercase tracking-[0.22em] font-semibold ${
                                mod.bonus
                                  ? "text-amber-600 dark:text-amber-400"
                                  : "text-[#2563EB] dark:text-[#06B6D4]"
                              }`}
                            >
                              {mod.bonus ? "Bonus" : `Module ${i + 1}`}
                            </span>
                            {mod.bonus && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-amber-400/20 text-amber-700 dark:text-amber-300 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em]">
                                <Sparkles className="h-2.5 w-2.5" /> Free
                              </span>
                            )}
                          </div>
                          <div className="mt-1 font-display text-base sm:text-lg font-semibold tracking-tight text-[#0F172A] dark:text-white truncate">
                            {mod.title}
                          </div>
                          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                            {mod.sessions.length} sessions
                            {!mod.bonus && (
                              <>
                                {" "}· Sessions {start}–{end}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </AccordionTrigger>

                    <AccordionContent
                      data-testid={`curriculum-module-content-${i}`}
                      className="px-5 sm:px-7 pb-5"
                    >
                      <ul className="mt-1 grid sm:grid-cols-2 gap-x-6 gap-y-2 border-t border-slate-200/80 dark:border-white/10 pt-4">
                        {mod.sessions.map((s, j) => {
                          const num = mod.bonus ? j + 1 : start + j;
                          return (
                            <li
                              key={s}
                              data-testid={`curriculum-session-${i}-${j}`}
                              className="flex items-start gap-3 text-sm text-slate-700 dark:text-slate-200 leading-relaxed py-1.5"
                            >
                              <span
                                className={`mt-0.5 flex-none inline-flex h-6 w-6 items-center justify-center rounded-md text-[10px] font-semibold ${
                                  mod.bonus
                                    ? "bg-amber-400/20 text-amber-700 dark:text-amber-300"
                                    : "bg-slate-100 dark:bg-white/[0.06] text-slate-500 dark:text-slate-400"
                                }`}
                              >
                                {String(num).padStart(2, "0")}
                              </span>
                              <span className="flex-1">{s}</span>
                            </li>
                          );
                        })}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>
              );
            })}
          </Accordion>
        </div>

        {/* Footer card */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-10 rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.04] p-6 sm:p-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 hairline"
        >
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#2563EB] dark:text-[#06B6D4] font-semibold">
              Complete Training
            </div>
            <div className="mt-1 font-display text-lg sm:text-xl font-semibold tracking-tight text-[#0F172A] dark:text-white">
              {moduleCount} modules · {sessionCount}+ project-based sessions
            </div>
          </div>
          <div className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#0F172A] via-[#1E3A8A] to-[#06B6D4] text-white px-4 py-2 text-xs font-semibold shadow-[0_8px_24px_-8px_rgba(6,182,212,0.55)]">
            <Sparkles className="h-3.5 w-3.5" /> Lifetime Access
          </div>
        </motion.div>
      </div>
    </section>
  );
}
